import { motion } from 'motion/react';
import styled, { css, keyframes } from 'styled-components';
import { pxToRem } from 'modules/spacing';
import { theme } from 'modules/tetrisTheme';
export const Divider = styled.div `
  width: ${pxToRem(1)};
  flex-shrink: 0;
  background-color: ${theme.swatches.warm[100]};
`;
export const FalconWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.circle};
  background-color: ${({ $state }) => ($state === 'success' ? theme.swatches.green[500] : theme.swatches.red[500])};

  [data-icon='falcon'] {
    color: ${theme.semantic.foreground.inverse};
  }
`;
const getSizeStyle = (size) => {
    switch (size) {
        case 'sm':
            return css `
        height: ${pxToRem(22)};
        border-radius: ${pxToRem(11)};
        gap: ${theme.spacing.sm};
        padding: ${theme.spacing.sm} ${theme.spacing.md};

        ${Divider} {
          height: ${pxToRem(11)};
        }

        ${FalconWrapper} {
          height: ${pxToRem(11)};
          width: ${pxToRem(11)};

          [data-icon='falcon'] {
            transform: scale(0.5);
          }
        }
      `;
        case 'md':
            return css `
        height: ${pxToRem(28)};
        border-radius: ${pxToRem(14)};
        gap: ${theme.spacing.md};
        padding: ${theme.spacing.md} ${theme.spacing.lg};

        ${Divider} {
          height: ${pxToRem(14)};
        }

        ${FalconWrapper} {
          height: ${pxToRem(14)};
          width: ${pxToRem(14)};

          [data-icon='falcon'] {
            transform: scale(0.65);
          }
        }
      `;
        case 'lg':
            return css `
        height: ${pxToRem(36)};
        border-radius: ${pxToRem(18)};
        gap: ${theme.spacing.md};
        padding: ${theme.spacing.lg};

        ${Divider} {
          height: ${pxToRem(18)};
        }

        ${FalconWrapper} {
          height: ${pxToRem(18)};
          width: ${pxToRem(18)};

          [data-icon='falcon'] {
            transform: scale(0.8);
          }
        }
      `;
        default:
            return undefined;
    }
};
const sweepAnimation = keyframes `
  0% {
    transform: translateX(-150%);
  }
    
  100% {
    transform: translateX(250%);
  }
`;
export const Container = styled(motion.div).attrs(() => ({
    animate: {
        opacity: 1,
    },
    initial: {
        opacity: 0,
    },
    transition: {
        type: 'tween',
        ease: [0.25, 0.1, 0.25, 1],
        duration: 0.375,
    },
})) `
  ${(props) => getSizeStyle(props.$size)};

  border: ${(p) => p.$border && `solid ${pxToRem(1)} ${theme.semantic.border.default}`};
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
  width: fit-content;

  &::before {
    content: '';
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    width: 80%;
    background: linear-gradient(90deg, transparent 0%, rgb(255 255 255 / 70%) 50%, transparent 100%);
    animation: ${sweepAnimation} 2s ease-in-out infinite;
    pointer-events: none;
    transition: opacity 250ms ease-out;
    opacity: ${({ $showAnimation }) => ($showAnimation ? 1 : 0)};
  }
`;
export const IconContainer = styled.div `
  flex-shrink: 0;
`;
const fadeInAnimation = keyframes `
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;
/**
 * Stays mounted once faded in, as `display: contents`, so the children are never remounted. The fade is a CSS
 * animation rather than motion: motion's accelerated opacity drops back to its initial value for a frame when it ends.
 */
export const ChildrenContainer = styled.div `
  ${({ $fadedIn }) => $fadedIn
    ? css `
          display: contents;
        `
    : css `
          position: inherit;
          display: inherit;
          flex: inherit;
          flex-direction: inherit;
          gap: inherit;
          overflow: inherit;
          animation: ${fadeInAnimation} 0.5s ease-out both;
        `}
`;
